/* The wisp: one breath in eight frames, plus the motes that breath sheds.
 *
 * A wisp has no gait to animate, so the eight frames spend themselves on the
 * three things a floating light actually does — it DRIFTS, it PULSES, and it
 * FLICKERS — and the trick is that those three run on different clocks. If
 * they all peaked together the cycle would read as one throb repeated twice.
 *
 * The two frames the game already had are the two extremes of the pulse, and
 * they are kept exactly. `wisp_0` is the wide body, 10 across, its veils
 * trailing three rows and two motes already well clear of the tail: that is
 * the end of an exhale, so it is frame 0. `wisp_1` is the tight body, 8
 * across, six rows tall, veils pulled in short: that is the end of the
 * inhale, so it is frame 4. Every frame between them changes the silhouette
 * by exactly one pixel per side per frame, which is why it reads as breathing
 * and not as two sprites being swapped.
 *
 * The three clocks:
 *
 *   PULSE (period 8, keyed to the two originals) — the body contracts from
 *   10x8 down to 8x6 across frames 0-4 and swells back out across 4-0. Width
 *   only ever goes 10 or 8, because there is no half pixel between them; the
 *   in-between frames spend their contraction on HEIGHT instead, which is
 *   also what stops any two frames sharing an exact outline — though 5, 6
 *   and 7 do share a bounding box; see DRIFT below.
 *
 *   DRIFT (period 8) — smaller than it looks, and worth saying plainly,
 *   because the obvious thing to write here is a sine on the body's centre
 *   and that is not what is drawn. The body does not translate; it hangs.
 *   Its TOP edge rides rows 2, 2, 1, 2, 3, 3, 3, 3 — up one into the flare,
 *   down one for the whole back half — while its BOTTOM edge rides 12, 11,
 *   11, 10, 10, 12, 12, 12, which is the tail retracting through the inhale
 *   and paying back out through the exhale. So the vertical read is a light
 *   hung from a nearly fixed point with its veils doing the moving, not a
 *   light sliding up and down.
 *
 *   The cost of the alternative is why. Frames 5, 6 and 7 share one outline
 *   box (rows 3..12) and that back half moves in colour and width rather
 *   than position, which looks like a stall worth fixing until you price it:
 *   translating a 65 px body down one row costs about 30 px of change, as
 *   much as an entire transition's budget. Sinking frame 6 alone takes the
 *   step spread from 1.3x the median to 1.6x and leaves that frame 62-90 px
 *   from every other one in the loop; spreading the sink over 5 and 6 still
 *   lands at 1.5x and drops the 4->5 silhouette overlap to 0.49. Growing
 *   downward is simply the cheap way to move a light down, and the even
 *   32-48 px steps this file does get are worth more than a literal drift.
 *
 *   FLICKER (period 8, offset again) — the white core counts 2, 4, 4, 2, 2,
 *   1, 0, 1. Frame 2 is the flare: the teal reaches all the way to the rim.
 *   Frame 6 is the guttering: no white left at all, the core dropped to `t`
 *   and its halo to the darker `k`, which is the one moment in the loop where
 *   you can see the thing is only barely lit. It is at its dimmest when it is
 *   at its lowest and fattest, and brightest when it is tight and high.
 *
 * The motes are the part that proves this is a loop rather than a rocking
 * chair. Each one is shed from the tail, lives four frames, and moves one
 * column out and one row down every frame while it does: born a single lit
 * `v`, opening into the `o`-ringed `V` diamond of the originals, then falling
 * to a lone dark speck at the edge and gone. Two generations are alive at any
 * time, four frames apart — one budding at 3 and 7, one at full diamond at 0
 * and 4 — so the shed happens twice per breath. The pair shed at frame 3 rides
 * one row higher than the pair shed at frame 7 for the whole of its life,
 * because at frame 3 the wisp was one row higher when it let go of them.
 *
 * The two generations do cross the same ground at different ages, though: the
 * frame-3 pair on its third frame and the frame-7 pair on its second both sit
 * at (2,11) and (13,11), so frames 0 and 5 carry pixel-identical rows 10-12
 * and are the closest non-adjacent pair in the loop at 29 px. Both ends of
 * that are pinned — frame 4 fixes where the first pair is, frame 0 fixes the
 * second — so it cannot be moved without editing an original.
 *
 * Mass is traded, not held: when the body pulls in, the veils run long and the
 * motes are out at the edges; when it swells, the tail is a stub and the motes
 * are specks or nothing. The trade narrows the swing but does not close it —
 * total ink runs 82, 72, 68, 56, 56, 68, 70, 64, and the body without its
 * motes runs 72, 62, 66, 54, 46, 58, 68, 62, a 1.6x spread. That spread is
 * INHERITED rather than introduced: `S.wisp_0` is a 72 px body and `S.wisp_1`
 * a 46 px one, so any cycle keeping both originals exactly has to cover the
 * same ground. A light is allowed to breathe this much. A walk would not be.
 *
 * Frame 7 hands back to 0 by rising one row at the top — the last pixel of
 * the inhale becoming the first of the next exhale.
 */

/** 0 — exhale complete. Widest body, longest veils, both motes clear of the
 *  tail. The original `S.wisp_0`, untouched. */
export const wisp_0 = [
  '................',
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovvvvvvo....',
  '...ovvtttvvvo...',
  '...ovvtwwtvvo...',
  '...ovvtttvvvo...',
  '....ovvvvvvo....',
  '.....ovvvvo.....',
  '..o...ovvo...o..',
  '.oVo..oVVo..oVo.',
  '..o....oo....o..',
  '................',
  '................',
  '................',
];

/** 1 — the draw begins. The body lifts its floor a row, the core kindles to a
 *  2x2 and rides high in the mass because the light leads the rise. The motes
 *  drop a row and slide a column out. */
export const wisp_1 = [
  '................',
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovtwwtvo....',
  '...ovvtwwtvvo...',
  '...ovvttttvvo...',
  '....ovvvvvvo....',
  '.....ovvvvo.....',
  '......ovvo......',
  '......oVVo......',
  '.o.....oo.....o.',
  'oVo..........oVo',
  '.o............o.',
  '................',
  '................',
];

/** 2 — the flare and the top of the drift. Narrow now, and taller than it is
 *  at any other phase; the teal has reached the rim on both core rows. The
 *  veils lag left because the body went up without them. The old motes are
 *  two dim specks in the corners, one frame from gone. */
export const wisp_2 = [
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovttttvo....',
  '....ottwwtto....',
  '....ottwwtto....',
  '....ovttttvo....',
  '....ovvvvvvo....',
  '.....ovvvvo.....',
  '......ovvo......',
  '.....oVVo.......',
  '......oo........',
  '................',
  'V..............V',
  '................',
  '................',
];

/** 3 — falling back off the flare. Core down to two whites, veils cut to a
 *  stub, and a fresh pair of sparks buds either side of the tail: the next
 *  generation of motes, still single lit pixels. */
export const wisp_3 = [
  '................',
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovvttvvo....',
  '....ovtwwtvo....',
  '....ovvttvvo....',
  '....ovvvvvvo....',
  '.....ovvvvo.....',
  '......ovvo......',
  '....v..oo..v....',
  '................',
  '................',
  '................',
  '................',
  '................',
];

/** 4 — inhale complete. Tightest body of the loop, core sunk to the middle of
 *  it, and the sparks shed at 3 have opened into full ringed diamonds. The
 *  original `S.wisp_1`, untouched. */
export const wisp_4 = [
  '................',
  '................',
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovvttvvo....',
  '....ovtwwtvo....',
  '....ovvttvvo....',
  '.....ovvvvo.....',
  '...o..ovvo..o...',
  '..oVo..oo..oVo..',
  '...o.......o....',
  '................',
  '................',
  '................',
  '................',
];

/** 5 — the swell starts and the light starts losing it. The body grows a row
 *  downward, the veils run long again, and the white core has collapsed to a
 *  single off-centre pixel — the first frame you can see it guttering. */
export const wisp_5 = [
  '................',
  '................',
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovvttvvo....',
  '....ovttwtvo....',
  '....ovvttvvo....',
  '....ovvvvvvo....',
  '.....ovvvvo.....',
  '..o...ovvo...o..',
  '.oVo..oVVo..oVo.',
  '..o....oo....o..',
  '................',
  '................',
  '................',
];

/** 6 — the low point, and the dark one. Widest again, its top edge a row below
 *  where frame 0 holds it, with no white anywhere: the core is `t` and its
 *  halo `k`, the whole body a stop dimmer. Tail down to a stub, motes down to
 *  specks. Its outline box is rows 3..12, the same as 5 and 7 — the darkness
 *  and the width are what carry this frame, not a change of position. */
export const wisp_6 = [
  '................',
  '................',
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovvvvvvo....',
  '...ovvkttkvvo...',
  '...ovvkttkvvo...',
  '...ovvvvvvvvo...',
  '....ovvvvvvo....',
  '.....ovvvvo.....',
  '......ovvo......',
  '.V.....oo.....V.',
  '................',
  '................',
  '................',
];

/** 7 — catching again. One white pixel back in the core, the body starting to
 *  climb, veils long and lagging right this time, and two new sparks budding
 *  where the next pair of diamonds will open in frame 0. */
export const wisp_7 = [
  '................',
  '................',
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovttttvo....',
  '...ovvtwttvvo...',
  '...ovvttttvvo...',
  '....ovvvvvvo....',
  '.....ovvvvo.....',
  '......ovvo......',
  '...v...oVVo.v...',
  '........oo......',
  '................',
  '................',
  '................',
];
